import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import { TOOLTIPAVERAGE, TOOLTIPCURRENT, TOOLTIPTOLERENCE, TOOLTIPVARIANCE } from '../../constants';
import { KronsTimeCheckRecord, TimeCheckProps } from './type';
import { useGetKronosTimeReasonablenessQuery } from './timeCheckSlice';
import { getMonthName } from '../../utils/dateFormat';
import './timeCheck.scss';


export const TimeCheck = ({
    inputMonth,
    inputYear,
}: TimeCheckProps) => {
    const toleranceList = [
        { label: '10%', value: 10 },
        { label: '20%', value: 20 },
        { label: '30%', value: 30 },
        { label: '40%', value: 40 },
        { label: '50%', value: 50 },
        { label: '60%', value: 60 },
        { label: '70% or more', value: 70 },
        { label: 'Show All', value: 0 },
    ];
    const [selectedTolerance, setSelectedTolerance] = useState<number>(40);
    const [workingHoursList, setWorkingHoursList] = useState<any[]>([]);

    const { data: toleranceHourList } = useGetKronosTimeReasonablenessQuery({
        month: inputMonth,
        year: inputYear
    }, {
        skip: !inputMonth || !inputYear
    });

    const kronsTimeCalculation = (kronsTimeData: any, record: any, tolerancedata: any) => {
        const currentMonthData = tolerancedata.filter(
            (item: any) =>
                item.month === inputMonth &&
                item.scorecard_id === record.scorecard_id
        );
        const workedHoursCurrent = currentMonthData.length === 0 ? 0 : currentMonthData[0]?.worked_hours_avg;
        const regularHoursCurrent = currentMonthData.length === 0 ? 0 : currentMonthData[0]?.regular_hours_avg;
        const scorecardData = tolerancedata.filter((item: any) =>
            item.month !== inputMonth &&
            item.scorecard_id === record.scorecard_id
        );

        let workedHoursAverage = null;
        let regularHoursAverage = null;
        let workedHoursVariance = null;
        let regularHoursVariance = null;

        if (scorecardData.length > 0) {
            const workedHourSum = scorecardData?.reduce((a: any, b: any) => a + b.worked_hours_sum, 0);
            const regularHourSum = scorecardData?.reduce((a: any, b: any) => a + b.reg_hours_sum, 0);
            const workedHoursCount = scorecardData?.reduce((a: any, b: any) => a + b.worked_hours_count, 0);
            const regularHoursCount = scorecardData?.reduce((a: any, b: any) => a + b.reg_hours_count, 0);

            workedHoursAverage = (workedHourSum / workedHoursCount)
            regularHoursAverage = (regularHourSum / regularHoursCount);
            workedHoursVariance = workedHoursAverage > 0 ?(Math.abs(workedHoursAverage - workedHoursCurrent) / workedHoursAverage) * 100 : 0;
            regularHoursVariance = regularHoursAverage > 0 ? (Math.abs(regularHoursAverage - regularHoursCurrent) / regularHoursAverage) * 100: 0;
        }

        kronsTimeData.push({
            scorecardVersion: record.scorecard_version_id,
            month: getMonthName(inputMonth),
            scorecardId: record.scorecard_id,
            scorecardName: record.scorecard_name,
            workedHoursCurrent: workedHoursCurrent.toFixed(2),
            workedHoursAverage: workedHoursAverage === null ? '--' : workedHoursAverage?.toFixed(2),
            workedHoursVariance: workedHoursVariance === null ? null : parseFloat(workedHoursVariance?.toFixed(2)),
            regularHoursCurrent: regularHoursCurrent.toFixed(2),
            regularHoursAverage: regularHoursAverage === null ? '--' : regularHoursAverage?.toFixed(2),
            regularHoursVariance: regularHoursVariance === null ? null : parseFloat(regularHoursVariance?.toFixed(2)),
        });
    };

    useEffect(() => {
        const kronsTimeData: KronsTimeCheckRecord[] = [];
        if (toleranceHourList?.data?.length > 0) {
            toleranceHourList?.data?.forEach((record: any) => {
                if (kronsTimeData.length === 0) {
                    kronsTimeCalculation(kronsTimeData, record, toleranceHourList?.data);
                }
                else if (kronsTimeData.length > 0 && !kronsTimeData.find((item: any) => item.scorecardId === record.scorecard_id)) {
                    kronsTimeCalculation(kronsTimeData, record, toleranceHourList?.data);
                }
            });

            if (selectedTolerance !== 0) {
                const selectedToleranceData = kronsTimeData.filter((item: any) =>
                    ((item.workedHoursVariance > selectedTolerance) || (item.regularHoursVariance > selectedTolerance))
                );
                setWorkingHoursList(selectedToleranceData);
            }
            else {
                setWorkingHoursList(kronsTimeData);
            }
        }
        else {
            setWorkingHoursList([]);
        }
    }, [toleranceHourList, selectedTolerance]);
    const workedHourHeaderTemplate = (columnName: string, tooltipContent: any) => {
        return (
            <>    <div className='row mt-2'>
                <div className='col-md-4' style={{ display: "flex", flexDirection: "row" }}>
                    <span>{columnName}</span>
                    <span style={{ marginLeft: "5px" }}>
                        <Tooltip target={`#${columnName.toLowerCase()}-tooltip`} position="top" id='tool-tip-style'>
                            <div className="tooltip-content">
                                <span style={{ minWidth: '5rem' }}>
                                    {tooltipContent}
                                </span>
                            </div>
                        </Tooltip>
                        <span id={`${columnName.toLowerCase()}-tooltip`}>
                            <i className="pi pi-question" style={{ color: 'white' }} />
                        </span>
                    </span>
                </div>
            </div>
            </>
        );
    };
   
    const varianceBodyTemplate = (columnName: string, rowData: any) => {
        let colorCode = '';
        let hourVariance = null;
        if (columnName === "Worked Hours") {
            hourVariance = rowData.workedHoursVariance === null ? '--' : `${rowData.workedHoursVariance} %`;
            colorCode = (selectedTolerance && rowData.workedHoursVariance > selectedTolerance) ? "#E23636" : "#495057";

        } else if (columnName === "Regular Hours") {
            hourVariance = rowData.regularHoursVariance === null ? '--' : `${rowData.regularHoursVariance} %`;
            colorCode = (selectedTolerance && rowData.regularHoursVariance > selectedTolerance) ? "#E23636" : "#495057";
        }
        return (
            < span style={{ color: colorCode }}>
                {hourVariance}
            </span>
        );
    };

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header="Scorecard" rowSpan={2} filterField='scorecardName' filter filterPlaceholder="Search by scorecard" />
                <Column header="Time Period"  rowSpan={2} />
                <Column header="Worked Hours" colSpan={3} />
                <Column header="Regular Hours" colSpan={3} />
            </Row>
            <Row>
                <Column header={workedHourHeaderTemplate("Current", TOOLTIPCURRENT)} sortable field="workedHoursCurrent" />
                <Column header={workedHourHeaderTemplate("Average", TOOLTIPAVERAGE)} sortable field="workedHoursAverage" />
                <Column header={workedHourHeaderTemplate("Variance", TOOLTIPVARIANCE)} sortable field="workedHoursVariance" />
                <Column header={workedHourHeaderTemplate("Current", TOOLTIPCURRENT)} sortable field="regularHoursCurrent" />
                <Column header={workedHourHeaderTemplate("Average", TOOLTIPAVERAGE)} sortable field="regularHoursAverage" />
                <Column header={workedHourHeaderTemplate("Variance", TOOLTIPVARIANCE)} sortable field="regularHoursVariance" />
            </Row>
        </ColumnGroup>
    );
    return (
        <>
            <div className="row mt-4">
                <div className='col-md-3' >
                    <div className='content'>
                        <label htmlFor="select-team">Tolerance Setting</label>
                        <Dropdown
                            id='select-tolerance'
                            data-testid='tolerance-dropdown'
                            filter
                            placeholder="Select Tolerance"
                            className="custom-dropdown"
                            options={toleranceList}
                            optionValue={'value'}
                            optionLabel={'label'}
                            value={selectedTolerance}
                            onChange={(e) => {
                                setSelectedTolerance(e.target.value);
                            }}
                        />
                    </div>
                </div>
                <div className='col-md-1' style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ marginTop: "30px" }}>
                        <Tooltip target="#hint-descript" position="top" id='tool-tip-style'>
                            <div className="tooltip-content">
                                <span style={{ minWidth: '5rem' }}>
                                    {TOOLTIPTOLERENCE}
                                </span>
                            </div>
                        </Tooltip>
                        <span id="hint-descript">
                            <i className="pi pi-question" style={{ color: 'white' }} />
                        </span>
                    </span>
                </div>
            </div>
            <div className='row mt-4'>
                <DataTable
                    value={workingHoursList}
                    id="time-check-table"
                    rows={5}
                    paginator={workingHoursList?.length > 5}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    emptyMessage="No records found."
                    headerColumnGroup={headerGroup}
                >
                    <Column
                        field="scorecardName"
                    />
                    <Column
                        field="month"
                    />
                    <Column
                        field='workedHoursCurrent'
                    />
                    <Column
                        field='workedHoursAverage'
                    />
                    <Column
                        field='workedHoursVariance'
                        body={(rowData: any) => varianceBodyTemplate("Worked Hours", rowData)}
                    />
                    <Column
                        field='regularHoursCurrent'
                    />
                    <Column
                        field='regularHoursAverage'
                    />
                    <Column
                        field='regularHoursVariance'
                        body={(rowData: any) => varianceBodyTemplate("Regular Hours", rowData)}

                    />
                </DataTable>
            </div >
        </>
    )
}
